import React, { useState, useEffect } from 'react';
import { decodeCertPayload } from '../utils/certificateUtils';
import { decodeCertPayloadWithStorage } from '../utils/firebaseStorageUtils';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import './certificate.css';

const buildAssetCandidates = (certId) => [
  `/assets/certificates/${certId}.png`,
  `/assets/${certId}.png`,
  `/certificates/${certId}.png`,
];

const loadFirstAvailableImage = async (paths) => {
  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index];
    // eslint-disable-next-line no-await-in-loop
    const ok = await new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = path;
    });
    if (ok) return path;
  }
  return null;
};

const CertificateViewPage = ({ certToken, certId: certIdProp }) => {
  const params = new URLSearchParams(window.location.search);
  const data = certToken || params.get('cert');
  const certIdFromQuery = certIdProp || params.get('certId');
  const [payload, setPayload] = useState(null);
  const [certificateImage, setCertificateImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initPayload = async () => {
      setError(null);

      if (certIdFromQuery) {
        setPayload({ certId: certIdFromQuery, name: '-', issuedAt: null });
        const matchedPath = await loadFirstAvailableImage(buildAssetCandidates(certIdFromQuery));
        if (matchedPath) {
          setCertificateImage(matchedPath);
        } else {
          setError('Error getting the certificate. Please contact us on WhatsApp at +91-7489017551');
        }
        setLoading(false);
        return;
      }

      if (!data) {
        setPayload(null);
        setLoading(false);
        return;
      }

      // Try decoding with storage support first, fallback to old format
      let decodedPayload = decodeCertPayloadWithStorage(data);
      if (!decodedPayload) {
        decodedPayload = decodeCertPayload(data);
      }
      setPayload(decodedPayload);

      // Fetch certificate image from Firebase if URL is available
      if (decodedPayload?.imageUrl) {
        fetch(decodedPayload.imageUrl)
          .then((response) => {
            if (!response.ok) throw new Error('Failed to fetch certificate image');
            return response.blob();
          })
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            setCertificateImage(url);
            setLoading(false);
          })
          .catch((fetchError) => {
            console.error('Error fetching certificate image:', fetchError);
            setError('Could not load certificate image. Certificate data is verified.');
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    };

    initPayload();
  }, [data, certIdFromQuery]);

  if (loading) {
    return (
      <div className="verify-wrap">
        <div className="verify-card">
          <h2 className="verify-title">Loading Certificate...</h2>
          <p>Please wait while we fetch your certificate.</p>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="verify-wrap">
        <div className="verify-card">
          <h2 className="verify-title">Certificate Not Found</h2>
          <p>This certificate link is invalid or corrupted.</p>
          <a className="cert-link" href="/">Go to Home</a>
        </div>
      </div>
    );
  }

  const issuedDate = '29-06-2026';

  const handleDownloadCertificate = () => {
    if (!certificateImage) return;
    const link = document.createElement('a');
    link.href = certificateImage;
    link.download = `${payload.certId || 'certificate'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="verify-wrap">
      <SEOMeta
        title="Student Certificate - Algodoor Training Institute"
        description="View and verify your Algodoor programming course completion certificate with QR code authentication."
        keywords="student certificate, verification, training completion"
        url="/cert-view"
        type="WebPage"
        noindex
      />
      <div className="verify-card">
        <h1 className="verify-title">Algodoor Certificate Verification</h1>
        {error ? (
          <span className="verify-error-badge">Unverified</span>
        ) : (
          <span className="verify-ok">Verified</span>
        )}

        {certificateImage && (
          <div className="verify-image-container">
            <img src={certificateImage} alt="Certificate" className="verify-certificate-image" />
          </div>
        )}

        {error && (
          <div className="verify-error-container">
            <p className="verify-error">{error}</p>
            <a
              href="https://wa.me/917489017551"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-link"
            >
              💬 Chat on WhatsApp
            </a>
          </div>
        )}

        {!error && (
          <div className="verify-grid">
            <strong>Certificate ID</strong>
            <span>{payload.certId}</span>
            <strong>Issued Date</strong>
            <span>{issuedDate}</span>
          </div>
        )}

        {certificateImage && !error && (
          <button type="button" className="cert-btn" onClick={handleDownloadCertificate} style={{ marginTop: '1rem', width: '100%' }}>
            ⬇️ Download Certificate
          </button>
        )}

        <p className="cert-sub">This page is generated from the QR code data attached to the certificate.</p>
        <a className="cert-link" href="/">Back to Algodoor</a>
      </div>
    </div>
  );
};

export default CertificateViewPage;
